const TOTAL_AMOUNT_FALLBACK_KEYS = ['totalAmountDue', 'amountExcludingLateFees', 'currentCharges', 'billAmount', 'totalAmount', 'amount'];

/**
 * Pick the bill amount that represents "Total bill amount" for the active utility. Priority:
 *   1. Subtotal from the service that matches `utilityTypeName` (case-insensitive)
 *   2. First non-null top-level total field (totalAmountDue, amountExcludingLateFees, ...)
 */
export function getExtractedTotalAmount(data, utilityTypeName) {
  if (!data) return null;
  if (utilityTypeName && Array.isArray(data.properties)) {
    const target = String(utilityTypeName).toLowerCase();
    for (const prop of data.properties) {
      const services = prop === null || prop === void 0 ? void 0 : prop.services;
      if (!Array.isArray(services)) continue;
      const match = services.find(service => String((service === null || service === void 0 ? void 0 : service.serviceType) || '').toLowerCase() === target);
      const subtotal = Number(match === null || match === void 0 ? void 0 : match.subtotal);
      if (Number.isFinite(subtotal)) return subtotal;
    }
  }
  const key = TOTAL_AMOUNT_FALLBACK_KEYS.find(k => data[k] !== undefined && data[k] !== null);
  const fallback = key ? Number(data[key]) : null;
  return Number.isFinite(fallback) ? fallback : null;
}

/**
 * The total the provider is to be paid for the whole bill file, for the AP push.
 *
 * Distinct from {@link getExtractedTotalAmount}, which narrows to the one utility being allocated:
 * when a bill covers several utilities or properties the extractor narrows the top-level totals to
 * the matched slice and records the untouched figures under `invoiceTotals` (with `wasNarrowed`).
 * The amount owed to the provider is the whole invoice, so it is read from there, and only falls
 * back to the top-level total for extractions made before `invoiceTotals` existed.
 */
export function getExtractedInvoiceTotal(data) {
  var _data$invoiceTotals;
  if (!data) return null;
  const candidates = [(_data$invoiceTotals = data.invoiceTotals) === null || _data$invoiceTotals === void 0 ? void 0 : _data$invoiceTotals.totalAmountDue, data.totalAmountDue];
  const found = candidates.find(value => Number.isFinite(Number(value)) && value !== null && value !== '');
  return found === undefined ? null : Number(found);
}

/** Strip leading [ErrorCode] from messages such as "[ServiceProviderNotFoundForProperty] None of the…". */
export function formatBracketedAutomationError(message) {
  const s = String(message).trim();
  const match = s.match(/^\[([^\]]+)]\s*(.+)$/);
  if (match) return match[2].trim();
  return s;
}

/** Flatten + de-duplicate user-facing strings from a list of candidate fields on the response. */
function collectMessageList(candidates) {
  const seen = new Set();
  const result = [];
  for (const c of candidates) {
    if (c == null) continue;
    const arr = Array.isArray(c) ? c : [c];
    for (const entry of arr) {
      let text = '';
      if (typeof entry === 'string') text = formatBracketedAutomationError(entry);else if (entry && typeof entry === 'object' && typeof entry.message === 'string') text = entry.message.trim();else if (entry != null) text = String(entry).trim();
      if (text && !seen.has(text)) {
        seen.add(text);
        result.push(text);
      }
    }
  }
  return result;
}

/** Pull human-readable messages from bill automation API error payloads. */
export function getBillAutomationErrorList(response) {
  var _data$clientContextVa;
  if (!response || typeof response !== 'object') return [];
  const data = response.data || {};
  return collectMessageList([response.errors, response.errorMessages, data.errors, data.errorMessages, typeof response.error === 'string' ? [response.error] : response.error,
  // Client-context rejections from the bill automation pipeline are blocking, treat them as errors
  data.clientRejectionMessage, data.clientRejectionMessages, (_data$clientContextVa = data.clientContextValidation) === null || _data$clientContextVa === void 0 ? void 0 : _data$clientContextVa.requiredUtilitiesRejectionMessage]);
}

/** Pull non-blocking warning messages from the bill automation API response. */
export function getBillAutomationWarningList(response) {
  var _data$clientContextVa2;
  if (!response || typeof response !== 'object') return [];
  const data = response.data || {};
  return collectMessageList([response.warnings, response.warningMessages, data.warnings, data.warningMessages, (_data$clientContextVa2 = data.clientContextValidation) === null || _data$clientContextVa2 === void 0 ? void 0 : _data$clientContextVa2.addressMismatchWarning]);
}